'use client'

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef
} from 'react'
import { useNavigationData } from './NavigationProvider'
import { resolveTrackedSelectedGameweek } from '@/lib/utils/gameweekNavigation'

interface GameweekData {
  id: string
  gameweek_number: number
  start_date: string | null
  end_date: string | null
  deadline: string
  status: 'past' | 'current' | 'future'
}

interface GameweekNavigationContextData {
  selectedGameweek: number
  availableGameweeks: GameweekData[]
  currentGameweek: number
  canNavigatePrevious: boolean
  canNavigateNext: boolean
  isLoading: boolean
  error: string | null
  navigateToPrevious: () => void
  navigateToNext: () => void
  navigateToGameweek: (gameweek: number) => void
}

const GameweekNavigationContext = createContext<GameweekNavigationContextData>({
  selectedGameweek: 0, // 0 = uninitialized, waiting for real data
  availableGameweeks: [],
  currentGameweek: 0, // 0 = no real data yet
  canNavigatePrevious: false,
  canNavigateNext: false,
  isLoading: false,
  error: null,
  navigateToPrevious: () => {},
  navigateToNext: () => {},
  navigateToGameweek: () => {}
})

interface GameweekNavigationProviderProps {
  children: ReactNode
}

export function GameweekNavigationProvider({ children }: GameweekNavigationProviderProps) {
  const { availableGameweeks, gameweekInfo, isLoading, error } = useNavigationData()
  const [selectedGameweek, setSelectedGameweek] = useState<number>(0) // Use 0 as uninitialized state
  const autoTrackedGameweekRef = useRef<number | null>(0)

  const currentGameweek = gameweekInfo?.currentGameweek // NO FALLBACK - only real data

  // Keep the selected gameweek aligned with the computed current gameweek until the user
  // intentionally browses to a historical week.
  useEffect(() => {
    if (!currentGameweek) return

    setSelectedGameweek((previousSelectedGameweek) => {
      const nextSelectedGameweek = resolveTrackedSelectedGameweek({
        autoTrackedGameweek: autoTrackedGameweekRef.current,
        currentGameweek,
        selectedGameweek: previousSelectedGameweek
      })

      if (nextSelectedGameweek === currentGameweek) {
        autoTrackedGameweekRef.current = currentGameweek
      }

      return nextSelectedGameweek
    })
  }, [currentGameweek])

  // Calculate navigation constraints
  const canNavigatePrevious = useCallback(() => {
    if (!availableGameweeks.length) return false

    // Find the earliest accessible gameweek (past gameweeks + current preparation gameweek)
    // Note: 'current' now includes preparation phase thanks to fantasy football logic
    const accessibleGameweeks = availableGameweeks.filter((gw) => gw.status !== 'future')
    if (!accessibleGameweeks.length) return false

    const earliestAccessible = Math.min(...accessibleGameweeks.map((gw) => gw.gameweek_number))
    return selectedGameweek > earliestAccessible
  }, [selectedGameweek, availableGameweeks])

  const canNavigateNext = useCallback(() => {
    if (!availableGameweeks.length) return false

    // Find the latest accessible gameweek (includes current preparation gameweek)
    // Note: Fantasy football logic ensures current gameweek is accessible during preparation
    const accessibleGameweeks = availableGameweeks.filter((gw) => gw.status !== 'future')
    if (!accessibleGameweeks.length) return false

    const latestAccessible = Math.max(...accessibleGameweeks.map((gw) => gw.gameweek_number))
    return selectedGameweek < latestAccessible
  }, [selectedGameweek, availableGameweeks])

  // Navigation functions
  const navigateToPrevious = useCallback(() => {
    if (!canNavigatePrevious()) return

    const accessibleGameweeks = availableGameweeks
      .filter((gw) => gw.status !== 'future' && gw.gameweek_number < selectedGameweek)
      .sort((a, b) => b.gameweek_number - a.gameweek_number)

    if (accessibleGameweeks.length > 0) {
      const nextGameweek = accessibleGameweeks[0].gameweek_number
      autoTrackedGameweekRef.current = nextGameweek === currentGameweek ? currentGameweek : null
      setSelectedGameweek(nextGameweek)
    }
  }, [canNavigatePrevious, availableGameweeks, currentGameweek, selectedGameweek])

  const navigateToNext = useCallback(() => {
    if (!canNavigateNext()) return

    const accessibleGameweeks = availableGameweeks
      .filter((gw) => gw.status !== 'future' && gw.gameweek_number > selectedGameweek)
      .sort((a, b) => a.gameweek_number - b.gameweek_number)

    if (accessibleGameweeks.length > 0) {
      const nextGameweek = accessibleGameweeks[0].gameweek_number
      autoTrackedGameweekRef.current = nextGameweek === currentGameweek ? currentGameweek : null
      setSelectedGameweek(nextGameweek)
    }
  }, [canNavigateNext, availableGameweeks, currentGameweek, selectedGameweek])

  const navigateToGameweek = useCallback(
    (gameweek: number) => {
      const targetGameweek = availableGameweeks.find((gw) => gw.gameweek_number === gameweek)
      if (!targetGameweek || targetGameweek.status === 'future') {
        console.warn(`Cannot navigate to gameweek ${gameweek}: not accessible`)
        return
      }

      autoTrackedGameweekRef.current = gameweek === currentGameweek ? currentGameweek : null
      setSelectedGameweek(gameweek)
    },
    [availableGameweeks, currentGameweek]
  )

  const contextValue: GameweekNavigationContextData = {
    selectedGameweek,
    availableGameweeks,
    currentGameweek: currentGameweek || 0, // Show 0 when no real data, never fake data
    canNavigatePrevious: canNavigatePrevious(),
    canNavigateNext: canNavigateNext(),
    isLoading,
    error,
    navigateToPrevious,
    navigateToNext,
    navigateToGameweek
  }

  return (
    <GameweekNavigationContext.Provider value={contextValue}>
      {children}
    </GameweekNavigationContext.Provider>
  )
}

export function useGameweekNavigation() {
  const context = useContext(GameweekNavigationContext)
  if (!context) {
    throw new Error('useGameweekNavigation must be used within a GameweekNavigationProvider')
  }
  return context
}
